<template>
  <div class="hpsfir-card bill">
    <HpsfirProTable
      ref="tableRef"
      title="订单管理"
      :paginations="{ pageSize: 10 }"
      page-layout="total, sizes, prev, pager, next"
      :columns="columns"
      :get-data="getData"
      :loading="loading"
      row-key="id"
      :top-operates="topOperates"
      :operates="operates"
      :fixed-operate="true"
      :codition-init-values="conditionsInitValue"
    />

    <!-- 订单详情弹窗 -->
    <OrderDetail v-model="showDetail" :order-data="selectedOrder" />
    <!-- 导出弹窗 -->
    <Export ref="exportRef" />
    <!-- 发货弹窗 -->
    <PostDialog v-model="showPost" :order-data="selectedOrder" @success="handleRefresh" />
    <!-- 处理售后弹窗 -->
    <ResolveDialog v-model="showResolve" :order-data="selectedOrder" @success="handleRefresh" />
  </div>
</template>
<script lang="ts" setup>
import {
  HpsfirProTable,
  HpsfirProTableColumn,
  HpsfirProTableData,
  HpsfirProTableFilter,
  HpsfirProTableGetDataFunction,
  useBaseAsync,
} from 'hpsfir-managebase-ui';
import { useApi } from '@/hooks/useApi';
import { ElMessage } from 'element-plus';
import { ref } from 'vue';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { generateOrderInfoDoc, generatePrintConfigDoc } from './docgenerator';
import OrderDetail from './detail.vue';
import Export from '@/components/Export.vue';
import PostDialog from './post.vue';
import ResolveDialog from './resolve.vue';

const columns = ref<HpsfirProTableColumn[]>([]);
const tableRef = ref<InstanceType<typeof HpsfirProTable>>();
const conditionsInitValue = {};
const exportRef = ref<InstanceType<typeof Export>>();

// 详情弹窗相关
const showDetail = ref(false);
const showPost = ref(false);
const showResolve = ref(false);
const selectedOrder = ref<any>({});
let paramsLast: any;
let tableDateLast: any;
// 订单状态选项
const statusOptions = [
  { label: '待付款', value: 'pending_payment' },
  { label: '已取消', value: 'cancelled' },
  { label: '待打印', value: 'pending_print' },
  { label: '待收货', value: 'pending_delivery' },
  { label: '已完成', value: 'completed' },
  { label: '已退款', value: '_re_refunded' },
  { label: '售后中', value: '_re_after_sales' },
];

const topOperates: any[] = [
  {
    key: 'export',
    name: '导出',
    handler: () => {
      exportRef.value?.show(
        'billApi',
        'pageAllBillbill',
        paramsLast,
        columns.value,
        10,
        tableDateLast.total,
      );
    },
  },
];

// 基础字段列定义
const getBasicColumns = (): HpsfirProTableColumn[] => [
  {
    name: 'id',
    label: '订单编号',
    type: 'numberStr',
    isCondition: true,
    width: 100,
    render: ({ row }: any) => String(row.id),
  },
  {
    name: 'status',
    label: '订单状态',
    type: 'select',
    isCondition: true,
    options: statusOptions,
    width: 120,
    render: ({ row }: any) => statusOptions.find((item) => item.value === row.status)?.label || '未知',
  },
];

// 价格字段列定义
const getPriceColumns = (): HpsfirProTableColumn[] => [

  {
    name: 'printPrice',
    label: '打印费',
    type: 'string',
    width: 80,
  },
  {
    name: 'postPrice',
    label: '邮费',
    type: 'string',
    width: 80,
  },
  {
    name: 'totalPrice',
    label: '合计价格',
    type: 'string',
    width: 100,
  },
  {
    name: 'discountPrice',
    label: '优惠金额',
    type: 'string',
    width: 100,
  },
  {
    name: 'realPrice',
    label: '实际价格',
    type: 'string',
    width: 100,
  },
  {
    name: 'backPrice',
    label: '退款金额',
    type: 'string',
    width: 100,
  },
];

// 其他字段列定义
const getOtherColumns = (): HpsfirProTableColumn[] => [
  {
    name: 'postcode',
    label: '快递单号',
    type: 'string',
    width: 120,
  },
  {
    name: 'reason',
    label: '售后原因',
    type: 'string',
    width: 150,
  },
  {
    name: 'processResult',
    label: '处理结果',
    type: 'string',
    width: 150,
  },
  {
    name: 'createTime',
    label: '创建时间',
    type: 'string',
    width: 160,
  },
];

// 创建列定义
const createColumns = () => [
  ...getBasicColumns(),
  ...getPriceColumns(),
  ...getOtherColumns(),
] as HpsfirProTableColumn[];

// 初始化
const init = useBaseAsync(() => {
  columns.value = createColumns();
});

// 发货操作
const shipBill = (row: any) => {
  selectedOrder.value = row;
  showPost.value = true;
};

// 处理售后操作
const processAfterSales = (row: any) => {
  selectedOrder.value = row;
  showResolve.value = true;
};

// 刷新表格
const handleRefresh = () => {
  tableRef.value?.refresh();
};

// 处理单个文件并添加到zip
const processFileToZip = async(zip: JSZip, print: any): Promise<boolean> => {
  try {
    const response = await fetch(print.url);
    if (!response.ok) {
      throw new Error(`下载失败: ${response.statusText}`);
    }

    const blob = await response.blob();
    let fileName = print.name;
    let counter = 1;
    while (zip.file(fileName)) {
      const nameParts = print.name.split('.');
      if (nameParts.length > 1) {
        const extension = nameParts.pop();
        const baseName = nameParts.join('.');
        fileName = `${baseName}(${counter}).${extension}`;
      } else {
        fileName = `${print.name}(${counter})`;
      }
      counter++;
    }

    zip.file(fileName, blob);
    return true;
  } catch (error) {
    ElMessage.error(`处理文件 ${print.name} 失败，已跳过`);
    return false;
  }
};

// 下载文件操作
const downloadFiles = useBaseAsync(async(row: any) => {
  try {
    if (!row.prints || row.prints.length === 0) {
      ElMessage.warning('该订单没有可下载的文件');
      return;
    }

    ElMessage.info(`开始打包下载 ${row.prints.length} 个文件...`);

    const zip = new JSZip();
    let successCount = 0;

    for (let i = 0; i < row.prints.length; i++) {
      const print = row.prints[i];
      const success = await processFileToZip(zip, print);
      if (success) {
        successCount++;
      }
    }

    // 生成文档
    const configDoc = generatePrintConfigDoc(row.prints);
    const orderDoc = generateOrderInfoDoc(row);

    // 添加文档到zip
    zip.file('打印配置说明.txt', configDoc);
    zip.file('订单信息.txt', orderDoc);

    if (successCount === 0) {
      ElMessage.error('没有文件可以下载，但仍会生成说明文档');
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const zipFileName = `订单_${row.id}.zip`;
    saveAs(zipBlob, zipFileName);

    const message = successCount > 0
      ? `压缩包下载成功！打包了 ${successCount} 个文件 + 订单信息 + 配置说明`
      : '压缩包下载成功！包含订单信息和配置说明文档';

    ElMessage.success(message);
  } catch (error) {
    ElMessage.error('文件打包下载失败');
  }
});

// 查看详情操作
const viewDetail = (row: any) => {
  selectedOrder.value = row;
  showDetail.value = true;
};

// 自定义操作
const operates = [
  {
    key: 'detail',
    name: '查看详情',
    handler: viewDetail,
  },
  {
    key: 'download',
    name: '下载文件',
    handler: downloadFiles,
  },
  {
    key: 'ship',
    name: '发货',
    handler: shipBill,
  },
  {
    key: 'afterSales',
    name: '处理售后',
    handler: processAfterSales,
  },
];

const loading = ref(false);

const getData = useBaseAsync<HpsfirProTableGetDataFunction>(async(filter: HpsfirProTableFilter) => {
  try {
    loading.value = true;
    paramsLast = {
      conditions: Object.entries(filter.conditions).reduce((conditions, [key, val]) => {
        if (val === null || val === undefined || val === '') {
          return conditions;
        }
        conditions[key] = val;
        return conditions;
      }, {} as any),

    };
    const res = await useApi().billApi.pageAllBillbill({
      ...paramsLast,
      pageQuery: {
        page: filter.paginations.page - 1,
        pageSize: filter.paginations.pageSize,
      },
    });

    const tableData: HpsfirProTableData = {
      total: res.data.data?.total || 0,
      data: res.data.data?.data?.map((item: any) => ({
        ...item,
        hideOp: {
          download: ['cancelled'].includes(item.status),
          ship: !['pending_print'].includes(item.status),
          afterSales: !['_re_after_sales'].includes(item.status),
        },
      })) || [],
    };
    tableDateLast = tableData;
    return tableData;
  } finally {
    loading.value = false;
  }
});

Promise.all([init()]);
</script>
<style lang="scss" scoped>
@import './index.scss';
</style>
